import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ExternalLink, ShieldCheck, Zap, Wallet, FileText } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { personalLoan, PERSONAL_LOAN_URL } from "../mock";

const featureIcons = [Zap, Wallet, ShieldCheck, FileText];

function ApplyButton({ className = "", children }) {
  return (
    <a
      href={PERSONAL_LOAN_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {children}
    </a>
  );
}

export default function PersonalLoans() {
  return (
    <main>
      <PageHero
        title="Personal Loans"
        crumb="Personal Loans"
        subtitle="Flexible personal loans up to $50,000 for whatever life brings — check your rate in about a minute with no impact to your credit score.*"
      />

      {/* Intro + primary CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-2 items-center">
          <Reveal>
            <div className="relative">
              <img
                src={personalLoan.image}
                alt="Couple reviewing personal loan options at home"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-8 right-4 sm:right-8 bg-[#0b1f3a] text-white rounded-2xl p-6 shadow-xl w-52">
                <div className="font-display text-4xl font-extrabold text-[#c6a15b]">$50K</div>
                <div className="text-sm text-slate-300 mt-1">borrow up to, with fixed monthly payments</div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#c6a15b]">Personal Lending</p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0b1f3a] mt-3">
              Money for life's moments
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Whether you're consolidating debt, tackling a home project, or covering an unexpected
              expense, a personal loan gives you a simple, fixed-rate way to get it done. Answer a few
              quick questions and compare real offers from a network of trusted lenders — all through
              one secure application.
            </p>

            <div className="mt-7 grid grid-cols-2 gap-4">
              {personalLoan.stats.map((s) => (
                <div key={s.label} className="rounded-xl bg-slate-50 ring-1 ring-slate-100 p-5 text-center">
                  <div className="font-display text-2xl font-extrabold text-[#0b1f3a]">{s.value}</div>
                  <div className="mt-1 text-xs text-slate-500 uppercase tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>

            <ApplyButton className="group mt-8 inline-flex items-center justify-center gap-2 rounded-md bg-[#c6a15b] px-7 py-3.5 font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition-colors shadow-sm">
              Check Your Rate
              <ExternalLink className="h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
            </ApplyButton>
            <p className="mt-3 text-xs text-slate-400">
              You'll be taken to our secure partner portal (Lendvious) to complete your request.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#c6a15b]">Why a Personal Loan</p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0b1f3a] mt-3">
              Simple, fast, and flexible
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {personalLoan.features.map((f, i) => {
              const Icon = featureIcons[i % featureIcons.length];
              return (
                <Reveal key={f.title} delay={(i % 4) * 90}>
                  <div className="h-full rounded-2xl bg-white p-7 ring-1 ring-slate-100 shadow-sm hover:shadow-xl transition-shadow">
                    <div className="h-14 w-14 rounded-xl bg-[#0b1f3a] flex items-center justify-center">
                      <Icon className="h-7 w-7 text-[#c6a15b]" />
                    </div>
                    <h3 className="font-display font-bold text-lg text-[#0b1f3a] mt-6">{f.title}</h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Uses */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-2 items-center">
          <Reveal>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#c6a15b]">What It's For</p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0b1f3a] mt-3">
              One loan, endless possibilities
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Personal loans are unsecured, so there's no collateral required. Use your funds for
              almost anything you need.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {personalLoan.uses.map((u) => (
                <div key={u} className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-[#c6a15b]/15 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="h-4 w-4 text-[#b08e46]" />
                  </span>
                  <span className="text-slate-700">{u}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <img
              src={personalLoan.imageAlt}
              alt="Person planning finances on a laptop"
              className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
            />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-[#0b1f3a] px-8 py-14 sm:px-14 text-center">
              <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#c6a15b]/15 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-[#c6a15b]/15 blur-3xl" />
              <div className="relative max-w-2xl mx-auto">
                <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white">
                  See your rate in about a minute
                </h2>
                <p className="mt-4 text-slate-300 text-lg">
                  Checking your rate is free and won't affect your credit score.* Get started with our
                  secure partner portal.
                </p>
                <ApplyButton className="group mt-8 inline-flex items-center justify-center gap-2 rounded-md bg-[#c6a15b] px-8 py-4 font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition-colors">
                  Check Your Rate
                  <ExternalLink className="h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
                </ApplyButton>
              </div>
            </div>
          </Reveal>
          <p className="mt-8 text-center text-xs text-slate-400 max-w-3xl mx-auto">
            *Checking your rate generates a soft credit inquiry, which is visible only to you and does not
            affect your credit score. Personal loans are offered through LTR Lending's partner network
            (Lendvious). Loan amounts, rates, and terms vary by lender and are subject to eligibility and
            approval. This is not a commitment to lend.
          </p>
        </div>
      </section>
    </main>
  );
}
